import React, { useState } from 'react'
import Modal from '@components/common/modal/Modal'
import {
  GROUP_ORDER_CANCELLATION_SECTION_1,
  GROUP_ORDER_CANCELLATION_SECTION_2,
  GROUP_ORDER_CANCELLATION_SECTION_3,
  GroupOrderCancellationType,
  GroupOrderCancellationReason,
} from '../../../../../src/constants'
import {
  Suborder,
  AllSubOrderCancellationResponse,
  CancelGroupOrderResponse,
  GroupOrder,
} from '@types'
import { CancellationForm } from './CancellationForm'
import { PreviewOrdersSection } from './PreviewOrderSection'
import { AdjustChefPayoutsSection } from './AdjustChefPayoutSection'

type CancelGroupOrderReq = {
  orderId: string
  cancellationType: GroupOrderCancellationType
  cancellationReason: GroupOrderCancellationReason
  cancellationNotes: string
  chefPayoutAdjustments: Record<string, number>
}

interface CancelGroupOrderModalProps {
  groupOrder: GroupOrder
  hideModal: () => void
  cancelGroupOrder: (
    req: CancelGroupOrderReq,
  ) => Promise<CancelGroupOrderResponse | null>
  cancelAndRefundAllSubOrders: (
    orderId: string,
  ) => Promise<AllSubOrderCancellationResponse>
  getEstimatedChefPayouts: (orderId: string) => Promise<Record<string, string>> // from backend as string -> m.Decimal
  getGroupOrderActiveSubOrders: (orderId: string) => Promise<Suborder[]>
}

const CancelGroupOrderModal: React.FC<CancelGroupOrderModalProps> = ({
  cancelAndRefundAllSubOrders,
  cancelGroupOrder,
  getEstimatedChefPayouts,
  getGroupOrderActiveSubOrders,
  groupOrder,
  hideModal,
}) => {
  const [currentSection, setCurrentSection] = useState(
    GROUP_ORDER_CANCELLATION_SECTION_1,
  )
  const [cancellationType, setCancellationType] =
    useState<GroupOrderCancellationType>(GroupOrderCancellationType.CLIENT)
  const [cancellationReason, setCancellationReason] =
    useState<GroupOrderCancellationReason>(
      GroupOrderCancellationReason.OFFICE_CLOSURE,
    )
  const [cancellationNotes, setCancellationNotes] = useState('')
  const [cancelGroupOrderResponse, setCancelGroupOrderResponse] =
    useState<CancelGroupOrderResponse | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSave = async (chefPayoutMap: Record<string, number>) => {
    setIsSubmitting(true)
    const req: CancelGroupOrderReq = {
      orderId: groupOrder.id,
      cancellationReason,
      cancellationType,
      cancellationNotes,
      chefPayoutAdjustments: chefPayoutMap,
    }
    const resp = await cancelGroupOrder(req)
    setIsSubmitting(false)
    setCancelGroupOrderResponse(resp)
  }

  const renderSections = () => {
    if (currentSection === GROUP_ORDER_CANCELLATION_SECTION_1) {
      return renderCancellationInputSection()
    } else if (currentSection === GROUP_ORDER_CANCELLATION_SECTION_2) {
      return renderPreviewOrdersToCancelSection()
    } else if (currentSection === GROUP_ORDER_CANCELLATION_SECTION_3) {
      return renderAdjustChefPayoutsSection()
    }
  }

  const renderCancellationInputSection = () => {
    return (
      <CancellationForm
        numberOfChefs={groupOrder.chefs.length}
        cancellationType={cancellationType}
        setCancellationType={setCancellationType}
        cancellationReason={cancellationReason}
        setCancellationReason={setCancellationReason}
        cancellationNotes={cancellationNotes}
        setCancellationNotes={setCancellationNotes}
        handleCancel={hideModal}
        continueToNextSection={() =>
          setCurrentSection(GROUP_ORDER_CANCELLATION_SECTION_2)
        }
      />
    )
  }

  const renderPreviewOrdersToCancelSection = () => {
    return (
      <PreviewOrdersSection
        orderId={groupOrder.id}
        onBack={() => setCurrentSection(GROUP_ORDER_CANCELLATION_SECTION_1)}
        continueToNextSection={() =>
          setCurrentSection(GROUP_ORDER_CANCELLATION_SECTION_3)
        }
        cancelAndRefundAllSubOrders={cancelAndRefundAllSubOrders}
        getGroupOrderActiveSubOrders={getGroupOrderActiveSubOrders}
      />
    )
  }

  const renderAdjustChefPayoutsSection = () => {
    return (
      <AdjustChefPayoutsSection
        chefs={groupOrder.chefs}
        orderId={groupOrder.id}
        isSubmitting={isSubmitting}
        cancelGroupOrderResponse={cancelGroupOrderResponse}
        onBack={() => setCurrentSection(GROUP_ORDER_CANCELLATION_SECTION_2)}
        onClose={hideModal}
        onSave={handleSave}
        getEstimatedChefPayouts={getEstimatedChefPayouts}
      />
    )
  }

  return (
    <Modal
      title="Cancel Group Order"
      maxWidth="1200px"
      width="1200px"
      hideModal={hideModal}
    >
      {renderSections()}
    </Modal>
  )
}

export default CancelGroupOrderModal
